import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { AbstractControl, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { CustomisableSection } from '@shared/models/customisable-section';
import { Product } from '@shared/models/product.model';
import { ProductHomepageService } from '@shared/services/product-homepage.service';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import { AccessibleClickDirective } from '@shared/shared/directives/accessible-click.directive';
import { ISiteSettings } from '@shared/shared/interfaces/site-settings.interfaces';
import { CustomisableSectionsService } from '@shared/shared/services/customisable-sections.service';
import { RecaptchaService } from '@shared/shared/services/recaptcha.service';
import { Observable, Subject, combineLatest } from 'rxjs';
import { filter, finalize, take, takeUntil } from 'rxjs/operators';
import { ReCaptchaComponent } from '../re-captcha/re-captcha.component';

const DEFAULT_TITLE = `Firstly, let's confirm your eligibility.`;
const DEFAULT_BODY = `Enter your Westpac credit card details to confirm your eligibility for this service. We will store your card type in your profile, but will not store your card details or charge your card without further confirmation from you.`;

@Component({
  selector: 'ten-popup-eligibility',
  templateUrl: './popup-eligibility.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgIf,
    ReactiveFormsModule,
    NgClass,
    NgFor,
    AsyncPipe,
    TranslateModule,
    ReCaptchaComponent,
    MatLegacyButtonModule,
    MatIconModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatLegacyProgressSpinnerModule,
    AccessibleClickDirective,
    MatDialogModule,
  ],
})
export class PopupEligibilityComponent implements OnInit, OnDestroy {
  private onDestroy = new Subject<void>();

  public products: Observable<Product[]> = this.productHomepageService.getProducts();
  public eligibilityForm: UntypedFormGroup;
  public eligibility_popup_title: string;
  public eligibility_popup_body: string;
  public captchaLanguage: string;
  public reCaptchaCustomisableSection = null;

  private firstNDigitsControl: AbstractControl;
  private lastNDigitsControl: AbstractControl;

  public firstNDigitsPlaceholderHidden = false;
  public lastNDigitsPlaceholderHidden = false;

  public firstNDigitsPlaceholderString: string;
  public lastNDigitsPlaceholderString: string;

  public firstNDigitsPlaceholderTAG: string;
  public lastNDigitsPlaceholderTAG: string;

  public firstNDigitsEligibilityPanLength: number;
  public lastNDigitsEligibilityPanLength: number;

  public loaded = false;
  public eligibilityType: string;
  public showSelectProduct = false;
  public homepageComponentName: string;
  public enableRecaptcha: boolean;
  public preloginHomepage = false;

  @ViewChild(ReCaptchaComponent, { static: false }) public captchaComp: ReCaptchaComponent;

  constructor(
    @Inject(MAT_DIALOG_DATA) data: any,
    public dialogRef: MatDialogRef<PopupEligibilityComponent>,
    private form_builder: UntypedFormBuilder,
    private productHomepageService: ProductHomepageService,
    private siteSettingsService: SiteSettingsService,
    private reCaptchaService: RecaptchaService,
    private cdr: ChangeDetectorRef,
    private customisableSectionsService: CustomisableSectionsService
  ) {
    this.eligibility_popup_title = data.title || DEFAULT_TITLE;
    this.eligibility_popup_body = data.body || DEFAULT_BODY;
    this.homepageComponentName = data.homePageComponentName;
  }

  public ngOnInit() {
    combineLatest([this.siteSettingsService.fetchSiteSettings(), this.products])
      .pipe(
        filter(([siteSettings, products]: [ISiteSettings, Product[]]) => !!siteSettings && !!products),
        take(1)
      )
      .subscribe(([siteSettings, products]: [ISiteSettings, Product[]]) => {
        if (!products.length) {
          throw new Error(`Missing configured products on ${siteSettings.domain}`);
        }

        this.captchaLanguage = siteSettings.language;
        this.eligibilityType = siteSettings?.eligibilities_and_selfregs;
        this.preloginHomepage = siteSettings?.switches?.pre_login_home_page;
        this.firstNDigitsEligibilityPanLength = siteSettings.eligibility_pan_length || 8;
        this.lastNDigitsEligibilityPanLength = 2;

        this.firstNDigitsPlaceholderString = `${new Array(this.firstNDigitsEligibilityPanLength).join('x')}x`;
        this.lastNDigitsPlaceholderString = `${new Array(this.lastNDigitsEligibilityPanLength).join('x')}x`;

        this.firstNDigitsPlaceholderTAG = `EligibilityPAN:First${this.firstNDigitsEligibilityPanLength}`;
        this.lastNDigitsPlaceholderTAG = `EligibilityPAN:Last${this.lastNDigitsEligibilityPanLength}`;

        const pattern_first_n_digits = new RegExp(`^[0-9]{${this.firstNDigitsEligibilityPanLength}}$`);
        const pattern_last_n_digits = new RegExp(`^[0-9]{${this.lastNDigitsEligibilityPanLength}}$`);

        const group = {
          first_n_digits: [null, [Validators.required, Validators.pattern(pattern_first_n_digits)]],
          last_n_digits: [null, [Validators.required, Validators.pattern(pattern_last_n_digits)]],
        };

        this.showSelectProduct = !!siteSettings.switches && siteSettings.switches.product_dropdown;

        if (!this.showSelectProduct) {
          if (products.length === 1) {
            Object.assign(group, { product_id: [products[0].id, Validators.required] });
          } else {
            console.warn(`Inconsistent products configuration. Expected 1, found ${products.length}`);
            console.warn(`Ignoring the switch and rendering the dropdown for ${products.length} products`);
            this.showSelectProduct = true;
          }
        }

        if (this.showSelectProduct) {
          Object.assign(group, { product_id: ['', Validators.required] });
        }

        this.eligibilityForm = this.form_builder.group(group);

        this.firstNDigitsControl = this.eligibilityForm.get('first_n_digits');
        this.lastNDigitsControl = this.eligibilityForm.get('last_n_digits');
        this.reCaptchaService.setReCaptcha(true);
        this.enableRecaptcha =
          siteSettings?.switches?.enable_recaptchas && siteSettings?.switches?.enable_eligibility_recaptcha;
        this.loaded = true;
        this.cdr.detectChanges();
      });

    this.getCustomisableSections();
  }

  private setCustomisableSections(customisableSections: CustomisableSection[]) {
    const reCaptchaFooterSection = customisableSections && customisableSections[1] ? customisableSections[1] : null;

    this.reCaptchaCustomisableSection = reCaptchaFooterSection;
    this.cdr.detectChanges();
  }

  private getCustomisableSections() {
    this.customisableSectionsService
      .getCustomisableSections(['footercontent', 'recaptcha_banner'])
      .pipe(takeUntil(this.onDestroy))
      .subscribe((customisableSections) => {
        this.setCustomisableSections(customisableSections);
      });
  }

  /**
   * Close the current instance of the popup dialog. Called when 'close' emitted value
   * has been detected from child form component.
   */
  public closePopUp() {
    this.dialogRef.close();
  }

  public confirm() {
    this.captchaComp.execute();
  }

  public onBlur() {
    this.firstNDigitsPlaceholderHidden = this.firstNDigitsControl.value ? true : false;
    this.lastNDigitsPlaceholderHidden = this.lastNDigitsControl.value ? true : false;
  }

  public checkEligibility(captchaToken: any) {
    if (this.eligibilityForm.invalid) return;

    this.productHomepageService
      .checkEligibility(
        {
          identifier: this.eligibilityForm.get('first_n_digits').value,
          identifier2: this.eligibilityForm.get('product_id')?.value,
          captchaToken,
        },
        this.eligibilityType,
        this.homepageComponentName
      )
      .pipe(
        finalize(() => {
          this.closePopUp();
        })
      )
      .subscribe();
  }

  public hasErrors(): boolean {
    return (
      (this.firstNDigitsControl.touched &&
        (this.firstNDigitsControl.hasError('pattern') || this.firstNDigitsControl.hasError('required'))) ||
      (this.lastNDigitsControl.touched &&
        (this.lastNDigitsControl.hasError('pattern') || this.lastNDigitsControl.hasError('required')))
    );
  }

  public ngOnDestroy() {
    this.reCaptchaService.setReCaptcha(false);
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
